import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Typography, message } from 'antd';
import debounce from 'lodash/debounce';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { ExcalidrawCanvas } from 'atoms';
import { WhiteboardErrorCode, useRenameWhiteboard, useSaveWhiteboardScene } from 'core';
import type { IWhiteboard } from 'core';

import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from '@excalidraw/excalidraw/types/types';
import type { ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';

import { useExcalidrawLangCode } from './WhiteboardViewer';
import { useWhiteboardImages } from './useWhiteboardImages';
import { useWhiteboardLibrarySync } from './useWhiteboardLibrarySync';

const AUTOSAVE_WAIT_MS = 2000;
const AUTOSAVE_MAX_WAIT_MS = 10000;
const NETWORK_RETRY_DELAY_MS = 5000;
// Client-side warning threshold ≈6MB; the server hard cap is 8MB (SCENE_TOO_LARGE).
// Since rev.3 the scene carries no image dataURLs (they live on S3), so this is
// now vector-geometry only and effectively unreachable — kept as a backstop
// against a pathological stroke count.
const SCENE_SIZE_WARN_BYTES = 6 * 1024 * 1024;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type SerializeFn = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  type: 'database' | 'local',
) => string;

interface ILatestScene {
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
}

interface IConflictState {
  /** The server's current sceneVersion returned with VERSION_CONFLICT. */
  serverVersion: number | null;
}

interface IWhiteboardEditor {
  board: IWhiteboard;
  refetchWhiteboard: () => Promise<IWhiteboard | null>;
}

/**
 * Teacher editor with autosave (spec 0010 §6):
 * - debounce 2s / maxWait 10s over excalidraw onChange; flush on unmount,
 *   visibilitychange(hidden) and beforeunload so closing the tab does not
 *   lose the last strokes;
 * - optimistic lock: expectedVersion tracks the last server response; on
 *   VERSION_CONFLICT autosave pauses and a modal offers "load server version"
 *   vs "overwrite with mine";
 * - network failure: one silent retry after 5s, then a warning with a manual
 *   retry button — the dirty buffer is never dropped.
 */
export const WhiteboardEditor = memo(({ board, refetchWhiteboard }: IWhiteboardEditor) => {
  const { t } = useTranslation();
  const langCode = useExcalidrawLangCode();

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [conflict, setConflict] = useState<IConflictState | null>(null);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [boardName, setBoardName] = useState(board.name);
  const [apiReady, setApiReady] = useState(false);

  const { saveScene } = useSaveWhiteboardScene();
  const { renameWhiteboard } = useRenameWhiteboard();

  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const serializeRef = useRef<SerializeFn | null>(null);
  const latestSceneRef = useRef<ILatestScene | null>(null);
  const versionRef = useRef(board.sceneVersion);
  const lastSavedJsonRef = useRef<string | null>(null);
  const pausedRef = useRef(false);
  const savingRef = useRef(false);
  const followUpRef = useRef(false);
  const sizeWarnedRef = useRef(false);
  const networkWarnedRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const performSaveRef = useRef<(allowAutoRetry?: boolean) => void>();

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // The unmount flush kicks off an async save whose completion must not
  // setState on an unmounted component.
  const setSaveStatusSafe = useCallback((status: SaveStatus) => {
    if (mountedRef.current) {
      setSaveStatus(status);
    }
  }, []);

  // serializeAsJSON comes from the same lazy chunk the canvas loads — this
  // resolves instantly once the editor is on screen, and keeps the ~1MB CJS
  // bundle out of the main chunk. Kept in a ref so beforeunload/unmount
  // flushes can serialize synchronously.
  useEffect(() => {
    let cancelled = false;
    import('@excalidraw/excalidraw').then((module) => {
      if (!cancelled) {
        serializeRef.current = module.serializeAsJSON as SerializeFn;
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const sendSave = useCallback(
    async (sceneJson: string, allowAutoRetry: boolean): Promise<void> => {
      savingRef.current = true;
      setSaveStatusSafe('saving');
      try {
        const result = await saveScene(board.id, sceneJson, versionRef.current);
        if (result.success) {
          // Any armed retry is now stale: it holds an older sceneJson and
          // would re-send it against the bumped version, sailing through the
          // optimistic lock and reverting the strokes we just saved.
          clearRetryTimer();
          versionRef.current = result.sceneVersion ?? versionRef.current + 1;
          lastSavedJsonRef.current = sceneJson;
          networkWarnedRef.current = false;
          setSaveStatusSafe('saved');
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.VERSION_CONFLICT) {
          // Another tab/lesson saved first. Pause autosave — no silent overwrite.
          pausedRef.current = true;
          if (mountedRef.current) {
            setConflict({ serverVersion: result.sceneVersion });
          }
          setSaveStatusSafe('error');
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.SCENE_TOO_LARGE) {
          message.error(t('whiteboard.errors.sceneTooLarge'));
          setSaveStatusSafe('error');
          return;
        }
        message.error(t('whiteboard.errors.saveFailed'));
        setSaveStatusSafe('error');
      } catch {
        // Network error: one silent auto-retry after 5s, then surface a
        // warning with a manual retry. The scene stays in latestSceneRef.
        if (allowAutoRetry && mountedRef.current) {
          clearRetryTimer();
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            // Re-enter performSave rather than replaying this closed-over
            // sceneJson: it re-serializes the CURRENT scene and honours
            // pausedRef/lastSavedJsonRef, so a retry can never resurrect an
            // older scene over a newer one or fight a conflict resolution.
            // allowAutoRetry=false keeps this to a single silent retry.
            performSaveRef.current?.(false);
          }, NETWORK_RETRY_DELAY_MS);
        } else {
          setSaveStatusSafe('error');
          if (!networkWarnedRef.current && mountedRef.current) {
            networkWarnedRef.current = true;
            message.warning(t('whiteboard.save.networkWarning'));
          }
        }
      } finally {
        savingRef.current = false;
        // Strokes drawn while this save was in flight queued a follow-up — run
        // it even if we just unmounted (tab switch): the mutation must still
        // reach the server or those strokes are lost. Every setState inside
        // goes through setSaveStatusSafe, so firing post-unmount is safe.
        if (followUpRef.current) {
          followUpRef.current = false;
          performSaveRef.current?.();
        }
      }
    },
    [board.id, clearRetryTimer, saveScene, setSaveStatusSafe, t],
  );

  /**
   * `allowAutoRetry=false` marks a run that is itself the 5s retry, so a
   * persistently offline teacher gets exactly one silent retry and then the
   * warning — not an endless chain of silent ones.
   */
  const performSave = useCallback(
    (allowAutoRetry = true) => {
      if (pausedRef.current) {
        return;
      }
      if (savingRef.current) {
        followUpRef.current = true;
        return;
      }
      const latest = latestSceneRef.current;
      const serialize = serializeRef.current;
      if (!latest || !serialize) {
        return;
      }
      // EMPTY files, deliberately (spec 0010 rev.3): excalidraw would inline
      // every pasted image as a base64 dataURL and a few screenshots blow past
      // the server's 8MB cap. The images go to S3 through useWhiteboardImages;
      // the scene keeps only the fileId references on the image elements —
      // which the server's pruning needs to see, so they MUST stay.
      const sceneJson = serialize(latest.elements, latest.appState, {}, 'database');
      if (sceneJson === lastSavedJsonRef.current) {
        return;
      }
      if (sceneJson.length > SCENE_SIZE_WARN_BYTES && !sizeWarnedRef.current) {
        sizeWarnedRef.current = true;
        message.warning(t('whiteboard.errors.sizeWarning'));
      }
      sendSave(sceneJson, allowAutoRetry);
    },
    [sendSave, t],
  );

  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  const debouncedSave = useMemo(
    () => debounce(performSave, AUTOSAVE_WAIT_MS, { maxWait: AUTOSAVE_MAX_WAIT_MS }),
    [performSave],
  );

  // Flush pending strokes when the tab hides, the page unloads, or the
  // component unmounts (navigation to another lesson tab).
  useEffect(() => {
    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') {
        debouncedSave.flush();
      }
    };
    const flushOnUnload = () => {
      debouncedSave.flush();
    };
    document.addEventListener('visibilitychange', flushOnHide);
    window.addEventListener('beforeunload', flushOnUnload);
    return () => {
      document.removeEventListener('visibilitychange', flushOnHide);
      window.removeEventListener('beforeunload', flushOnUnload);
      debouncedSave.flush();
      clearRetryTimer();
    };
  }, [debouncedSave, clearRetryTimer]);

  // Scene images → S3 + hydration back into the canvas (spec 0010 rev.3).
  // Declared above handleChange: onImagesChange must exist before the change
  // handler closes over it.
  const { onImagesChange } = useWhiteboardImages({
    apiRef,
    apiReady,
    files: board.files,
    whiteboardId: board.id,
  });

  const handleChange = useCallback(
    (elements: readonly ExcalidrawElement[], appState: AppState, files: BinaryFiles) => {
      latestSceneRef.current = { elements, appState, files };
      // Upload BEFORE the conflict gate and outside the debounce: an image
      // must reach S3 even while autosave is paused on a conflict, otherwise
      // resolving the conflict would save elements pointing at nothing.
      onImagesChange(files);
      if (pausedRef.current) {
        return;
      }
      debouncedSave();
    },
    [debouncedSave, onImagesChange],
  );

  const handleRetry = useCallback(() => {
    networkWarnedRef.current = false;
    performSave();
  }, [performSave]);

  // Conflict resolution: adopt the server scene (refetch + updateScene +
  // adopt its sceneVersion)…
  const handleConflictLoadServer = useCallback(async () => {
    setConflictBusy(true);
    try {
      const fresh = await refetchWhiteboard();
      if (fresh) {
        versionRef.current = fresh.sceneVersion;
        lastSavedJsonRef.current = null;
        setBoardName(fresh.name);
        const api = apiRef.current;
        if (api && fresh.scene) {
          api.updateScene({
            elements: fresh.scene.elements as unknown as ExcalidrawElement[],
            ...(fresh.scene.appState
              ? { appState: fresh.scene.appState as unknown as AppState }
              : {}),
          });
          if (fresh.scene.files) {
            api.addFiles(Object.values(fresh.scene.files) as BinaryFileData[]);
          }
        }
      }
      setConflict(null);
      pausedRef.current = false;
      setSaveStatus('saved');
    } catch {
      message.error(t('whiteboard.errors.loadFailed'));
    } finally {
      setConflictBusy(false);
    }
  }, [refetchWhiteboard, t]);

  // …or overwrite with the local scene by resending against the version the
  // server reported in the conflict.
  const handleConflictOverwrite = useCallback(() => {
    if (conflict?.serverVersion != null) {
      versionRef.current = conflict.serverVersion;
    }
    setConflict(null);
    pausedRef.current = false;
    lastSavedJsonRef.current = null;
    performSave();
  }, [conflict, performSave]);

  const handleRename = useCallback(
    async (nextName: string) => {
      const trimmed = nextName.trim();
      if (!trimmed || trimmed === boardName) {
        return;
      }
      const previousName = boardName;
      setBoardName(trimmed);
      try {
        const result = await renameWhiteboard(board.id, trimmed);
        if (!result.success) {
          setBoardName(previousName);
          message.error(t('whiteboard.errors.renameFailed'));
        }
      } catch {
        setBoardName(previousName);
        message.error(t('whiteboard.errors.renameFailed'));
      }
    },
    [board.id, boardName, renameWhiteboard, t],
  );

  const setExcalidrawApi = useCallback((api: ExcalidrawImperativeAPI) => {
    apiRef.current = api;
    // Signals the library sync that the imperative API is available —
    // hydration needs both the API and the resolved library query.
    setApiReady(true);
  }, []);

  // Shared school component library: hydrate from the server + push teacher
  // additions back (spec 0010 rev.2). Never interferes with the scene autosave.
  const { onLibraryChange } = useWhiteboardLibrarySync(apiRef, apiReady);

  // Mount-only snapshot: the canvas reads initialData once; later board prop
  // updates flow through updateScene (conflict path) instead.
  const initialData = useMemo<ExcalidrawInitialDataState | null>(
    () =>
      board.scene
        ? ({
            elements: board.scene.elements,
            appState: board.scene.appState,
            files: board.scene.files,
            scrollToContent: true,
          } as unknown as ExcalidrawInitialDataState)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <EditorWrapper>
      <EditorHeader>
        <BoardName
          editable={{
            onChange: handleRename,
            tooltip: t('whiteboard.rename'),
          }}>
          {boardName || t('whiteboard.unnamed')}
        </BoardName>
        <SaveIndicator $status={saveStatus}>
          {saveStatus === 'saving' && t('whiteboard.save.saving')}
          {saveStatus === 'saved' && t('whiteboard.save.saved')}
          {saveStatus === 'error' && (
            <>
              {t('whiteboard.save.error')}
              <Button size="small" onClick={handleRetry}>
                {t('whiteboard.save.retry')}
              </Button>
            </>
          )}
        </SaveIndicator>
      </EditorHeader>

      <ExcalidrawCanvas
        initialData={initialData}
        onChange={handleChange}
        excalidrawApiRef={setExcalidrawApi}
        onLibraryChange={onLibraryChange}
        langCode={langCode}
      />

      <Modal
        open={Boolean(conflict)}
        title={t('whiteboard.conflict.title')}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText={t('whiteboard.conflict.loadServer')}
        cancelText={t('whiteboard.conflict.overwrite')}
        okButtonProps={{ loading: conflictBusy }}
        cancelButtonProps={{ disabled: conflictBusy }}
        onOk={handleConflictLoadServer}
        onCancel={handleConflictOverwrite}>
        {t('whiteboard.conflict.description')}
      </Modal>
    </EditorWrapper>
  );
});

const EditorWrapper = styled.div`
  width: 100%;
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const BoardName = styled(Typography.Text)`
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const SaveIndicator = styled.div<{ $status: SaveStatus }>`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${({ $status }) => ($status === 'error' ? Colors.signal_red : Colors.gray_dark)};
`;
